/**
 * Live chat integration point.
 *
 * KleipData does not currently bundle a third-party live-chat widget. This
 * module is the single, isolated place to connect one later: when a chat
 * service is added, implement `openLiveChatWidget()` to open it (for example by
 * calling the provider's JS API on `window`). Every "Start Live Chat" / "Start
 * Chat" control on the site routes through `startLiveChat()`, so wiring it here
 * is all that is required.
 *
 * No third-party provider is assumed or loaded here.
 */

export const LIVE_CHAT_EVENT = "kleipdata:open-live-chat";

type LiveChatBridge = { open?: () => void };

declare global {
  interface Window {
    kleipdataLiveChat?: LiveChatBridge;
  }
}

/** Attempt to open a connected live-chat widget. Returns true if one handled it. */
function openLiveChatWidget(): boolean {
  if (typeof window === "undefined") return false;

  // Let any future integration listen for a single, well-known event.
  window.dispatchEvent(new CustomEvent(LIVE_CHAT_EVENT));

  if (typeof window.kleipdataLiveChat?.open === "function") {
    window.kleipdataLiveChat.open();
    return true;
  }

  return false;
}

export type LiveChatResult = { connected: boolean };

/**
 * Entry point for every live-chat control. When no chat service is connected
 * yet, callers should surface the email fallback rather than failing silently.
 */
export function startLiveChat(): LiveChatResult {
  return { connected: openLiveChatWidget() };
}
